import React from "react";

import withStyles from "@material-ui/core/styles/withStyles";
import AddAlert from "@material-ui/icons/AddAlert";
import { AuthConsumer } from "react-check-auth";

import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import CustomTabs from "components/CustomTabs/CustomTabs.jsx";
import Snackbar from "components/Snackbar/Snackbar.jsx";

import MachineErrors from "../../components/Alerts/MachineErrors";
import ThresholdAlerts from "../../components/Alerts/ThresholdAlerts";
import OpenTillAlerts from "../../components/Alerts/OpenTillAlerts";
import ReplicationStatus from "../../components/Alerts/ReplicationStatus";
import AlertSettings from "../../components/Alerts/AlertSettings";

import httpr from "lib/httpRequest";
import { BASE_URL, isLocal } from "lib/config";

const styles = {
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
};

class Alerts extends React.Component {
  _isMounted = false;
  constructor(props) {
    super(props);

    this.state = {
      snackbarOpen: false,
      snackbarMsg: "",
      snackbarIcon: AddAlert,
      snackbarColor: "danger",
      minThreshold: 50,
      maxThreshold: 50,
      openTillDelay: 8,
      repStatusDelay: 24,
      memberSettings: {
        machineErrorRange: "", //"day" "week" "month" "year"
        minThreshold: 50,
        maxThreshold: 50,
        openTillDelay: 8,
        repStatusDelay: 24,
      },
    };
  }

  componentDidMount() {
    window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true; // MUI topography migration bypass
    this._isMounted = true;
    // get member settings
    this.getMemberAppSettings();
  }

  componentDidUpdate() {}

  componentWillUnmount() {
    this._isMounted = false;
  }

  getMemberAppSettings = () => {
    const token = localStorage.getItem("token");
    const fetch = httpr(token);

    fetch
      .get(`${BASE_URL}/api/members/settings/app`)
      .then((response) => {
        const settings = response.data[0];

        const newSettings = {
          machineErrorRange: settings.MachineErrorTimeInterval,
          minThreshold: settings.MinThreshold,
          maxThreshold: settings.MaxThreshold,
          openTillDelay: settings.OpenTillDelay,
          repStatusDelay: settings.RepStatusDelay,
        };

        if (this._isMounted === true) {
          this.setState({ memberSettings: newSettings });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  saveMemberAppSettings = () => {
    const token = localStorage.getItem("token");
    const fetch = httpr(token);

    fetch
      .post(`${BASE_URL}/api/members/settings/app/update`, {
        newSettings: this.state.memberSettings,
      })
      .then((response) => {
        if (response.data[0]["response"]) {
          const message = response.data[0]["response"];
          // show notification
          this.showNotification(message, AddAlert, "info");
        } else {
          this.showNotification(response.data[0]["error"], AddAlert, "danger");
        }
      });
  };

  // Dismisses Error or Open Till Alert and responds with a snackbox alert
  dismissAlert = (type, locationName, alert) => {
    const token = localStorage.getItem("token");
    const fetch = httpr(token);

    fetch
      .post(`${BASE_URL}/api/alerts/dismiss`, {
        alert,
        locationName,
        type,
      })
      .then((response) => {
        this.showNotification(response.data.message, AddAlert, "danger");
      })
      .catch((error) => {
        console.log(error);
      });
  };

  showNotification = (msg, icon, color) => {
    if (this._isMounted === true) {
      this.setState({
        snackbarOpen: true,
        snackbarMsg: msg,
        snackbarIcon: icon,
        snackbarColor: color,
      });
    }
  };

  handleErrorRangeChanged = (value) => (event) => {
    let memberSettings = { ...this.state.memberSettings };
    memberSettings.machineErrorRange = value;

    // If setting changed, change state and save setting in db
    if (this.state.memberSettings.machineErrorRange === value) {
      return;
    } else {
      this.setState({ memberSettings });
    }
  };

  handleMinThresholdChanged = (event) => {
    let memberSettings = { ...this.state.memberSettings };
    memberSettings.minThreshold = event;

    this.setState({ memberSettings: memberSettings });
  };

  handleMaxThresholdChanged = (event) => {
    let memberSettings = { ...this.state.memberSettings };
    memberSettings.maxThreshold = event;

    this.setState({ memberSettings: memberSettings });
  };

  handleOpenTillDelayChange = (OpenTillDelay) => {
    let memberSettings = { ...this.state.memberSettings };
    memberSettings.openTillDelay = OpenTillDelay;

    this.setState({ memberSettings: memberSettings });
  };

  handleRepStatusDelayChange = (RepStatusDelay) => {
    let memberSettings = { ...this.state.memberSettings };
    memberSettings.repStatusDelay = RepStatusDelay;

    this.setState({ memberSettings: memberSettings });
  };

  render() {
    return (
      <AuthConsumer>
        {({ isLoading: authLoading, userInfo, error }) => {
          if (authLoading) {
            return <div />;
          }

          if (!userInfo || !userInfo.member) {
            return <div />;
          }

          const { member } = userInfo;
          const locationLicenses = (member && member.LocationLicenses) ? member.LocationLicenses : [];

          const tabs = isLocal == true ? [
            {
              tabName: "Machine Errors",
              tabContent: (
                <div>
                  <AlertSettings
                    type="errors"
                    memberSettings={this.state.memberSettings}
                    handleErrorRangeChanged={this.handleErrorRangeChanged}
                    saveMemberAppSettings={this.saveMemberAppSettings}
                  />
                  <MachineErrors
                    classes={this.props.classes}
                    dismissAlert={this.dismissAlert}
                    memberSettings={this.state.memberSettings}
                    locationLicenses={locationLicenses}
                  />
                </div>
              ),
            },
            {
              tabName: "Threshold Alerts",
              tabContent: (
                <div>
                  <AlertSettings
                    type="threshold"
                    memberSettings={this.state.memberSettings}
                    handleMinThresholdChanged={this.handleMinThresholdChanged}
                    handleMaxThresholdChanged={this.handleMaxThresholdChanged}
                    saveMemberAppSettings={this.saveMemberAppSettings}
                  />
                  <ThresholdAlerts
                    classes={this.props.classes}
                    memberSettings={this.state.memberSettings}
                    locationLicenses={locationLicenses}
                  />
                </div>
              ),
            },
            {
              tabName: "Open Till Alerts",
              tabContent: (
                <div>
                  <AlertSettings
                    type="openTill"
                    memberSettings={this.state.memberSettings}
                    handleOpenTillDelayChange={this.handleOpenTillDelayChange}
                    saveMemberAppSettings={this.saveMemberAppSettings}
                  />

                  <OpenTillAlerts
                    classes={this.props.classes}
                    dismissAlert={this.dismissAlert}
                    memberSettings={this.state.memberSettings}
                    locationLicenses={locationLicenses}
                  />
                </div>
              ),
            },
          ] : [
            {
              tabName: "Machine Errors",
              tabContent: (
                <div>
                  <AlertSettings
                    type="errors"
                    memberSettings={this.state.memberSettings}
                    handleErrorRangeChanged={this.handleErrorRangeChanged}
                    saveMemberAppSettings={this.saveMemberAppSettings}
                  />
                  <MachineErrors
                    classes={this.props.classes}
                    dismissAlert={this.dismissAlert}
                    memberSettings={this.state.memberSettings}
                    locationLicenses={locationLicenses}
                  />
                </div>
              ),
            },
            {
              tabName: "Threshold Alerts",
              tabContent: (
                <div>
                  <AlertSettings
                    type="threshold"
                    memberSettings={this.state.memberSettings}
                    handleMinThresholdChanged={this.handleMinThresholdChanged}
                    handleMaxThresholdChanged={this.handleMaxThresholdChanged}
                    saveMemberAppSettings={this.saveMemberAppSettings}
                  />
                  <ThresholdAlerts
                    classes={this.props.classes}
                    memberSettings={this.state.memberSettings}
                    locationLicenses={locationLicenses}
                  />
                </div>
              ),
            },
            {
              tabName: "Open Till Alerts",
              tabContent: (
                <div>
                  <AlertSettings
                    type="openTill"
                    memberSettings={this.state.memberSettings}
                    handleOpenTillDelayChange={this.handleOpenTillDelayChange}
                    saveMemberAppSettings={this.saveMemberAppSettings}
                  />

                  <OpenTillAlerts
                    classes={this.props.classes}
                    dismissAlert={this.dismissAlert}
                    memberSettings={this.state.memberSettings}
                    locationLicenses={locationLicenses}
                  />
                </div>
              ),
            },
            {
              tabName: "Replication Status",
              tabContent: (
                <div>
                  <AlertSettings
                    type="replication"
                    memberSettings={this.state.memberSettings}
                    handleRepStatusDelayChange={this.handleRepStatusDelayChange}
                    saveMemberAppSettings={this.saveMemberAppSettings}
                  />
                  <ReplicationStatus
                    classes={this.props.classes}
                    memberSettings={this.state.memberSettings}
                    locationLicenses={locationLicenses}
                  />
                </div>
              ),
            },
          ];

          return (
            <div>
              <Snackbar
                place="tc"
                color={this.state.snackbarColor}
                icon={this.state.snackbarIcon}
                message={this.state.snackbarMsg}
                open={this.state.snackbarOpen}
                closeNotification={() => this.setState({ snackbarOpen: false })}
              />
              <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                  <CustomTabs title="Alerts:" tabs={tabs} headerColor="scmain" />
                </GridItem>
              </GridContainer>
            </div>
          );
        }}
      </AuthConsumer>
    );
  }
}

export default withStyles(styles)(Alerts);



// WEBPACK FOOTER //
// ./src/views/Alerts/Alerts.jsx